import { Popover, Transition, Tab } from '@headlessui/react';
import { IconTypography } from '@tabler/icons-react';
import React, { memo, useCallback, useMemo } from 'react';
import SpeakerLabelFontSelector from './SpeakerLabelTypographyMenu/SpeakerLabelFontSelector';
import SpeakerLabelUppercaseToggle from './SpeakerLabelTypographyMenu/SpeakerLabelUppercaseToggle';
import SpeakerLabelLineHeightInput from './SpeakerLabelTypographyMenu/SpeakerLabelLineHeightInput';
import SpeakerLabelFontSizeSelector from './SpeakerLabelTypographyMenu/SpeakerLabelFontSizeSelector';
import SpeakerLabelLetterSpacingInput from './SpeakerLabelTypographyMenu/SpeakerLabelLetterSpacingInput';
import { classnames } from '@/libs/utils';
import { updateSpeakerLabelStyles } from '@/stores/clip';
import PlayerMenuTooltip from '@/App/remotion/components/PlayerMenus/PlayerMenuTooltip';
import { Clip } from '@/player-config/src/types/clip-types';
import { SpeakerLabelTextStyle } from '@/player-config/src/types/asset-types';
import AlertBanner from '@/components/atoms/AlertBanner';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { getClipBroadcastId } from '@/player-config/src/utils/clipUtils';
import {
  cssToPreset,
  DEFAULT_NAME_FONT_SIZE_PRESET,
  DEFAULT_TITLE_FONT_SIZE_PRESET,
  FontSizePreset
} from '@/player-config/src/types/speaker-label-font-size';
import { selectSpeakerDetails } from '@/player-config/src/selectors/contentMetadataStoreSelectors';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';

function SpeakerLabelTypographyMenu({ clip, speakerId }: { clip: Clip; speakerId: string }) {
  const nameStyle = useMemo(
    () => clip.asset_metadata.speaker_label_styles?.nameStyle || {},
    [clip.asset_metadata.speaker_label_styles?.nameStyle]
  );
  const titleStyle = useMemo(
    () => clip.asset_metadata.speaker_label_styles?.titleStyle || {},
    [clip.asset_metadata.speaker_label_styles?.titleStyle]
  );

  const nameFontLocation = useMemo(() => {
    return clip.asset_metadata.speaker_label_styles?.font_location || '';
  }, [clip.asset_metadata.speaker_label_styles?.font_location]);

  const titleFontLocation = useMemo(() => {
    return (
      clip.asset_metadata.speaker_label_styles?.title_font_location ||
      clip.asset_metadata.speaker_label_styles?.font_location ||
      ''
    );
  }, [
    clip.asset_metadata.speaker_label_styles?.title_font_location,
    clip.asset_metadata.speaker_label_styles?.font_location
  ]);

  const updateNameStyle = useCallback(
    (updates: SpeakerLabelTextStyle) => {
      updateSpeakerLabelStyles(clip.id, {
        nameStyle: { ...nameStyle, ...updates }
      });
    },
    [clip.id, nameStyle]
  );

  const updateTitleStyle = useCallback(
    (updates: SpeakerLabelTextStyle) => {
      updateSpeakerLabelStyles(clip.id, {
        titleStyle: { ...titleStyle, ...updates }
      });
    },
    [clip.id, titleStyle]
  );

  const handleNameFontChange = useCallback(
    (fontName: string, fontLocation: string) => {
      // Update the main font_location field (used by name)
      updateSpeakerLabelStyles(clip.id, { font_location: fontLocation, nameStyle: { fontFamily: fontName } });
    },
    [clip.id]
  );

  const handleTitleFontChange = useCallback(
    (fontName: string, fontLocation: string) => {
      // Update the title_font_location field (separate from name)
      updateSpeakerLabelStyles(clip.id, {
        title_font_location: fontLocation,
        titleStyle: {
          fontFamily: fontName
        }
      });
    },
    [clip.id]
  );

  // Get speaker details from the store
  const speakersWithDetails = useContentMetadataStore(selectSpeakerDetails(getClipBroadcastId(clip) ?? ''));

  // Check for missing speaker data
  const speakerData = useMemo(() => {
    // Find speaker by their id field, not by using speakerId as the key
    const speaker = Object.values(speakersWithDetails).find(s => s.id === speakerId);

    if (!speaker) {
      return {
        hasName: false,
        hasTitle: false,
        speakerId
      };
    }

    return {
      hasName: !!(speaker.first_name && speaker.last_name),
      hasTitle: !!(speaker.title || speaker.company),
      speakerId
    };
  }, [speakersWithDetails, speakerId]);

  const handleEditSpeaker = useCallback(() => {
    if (speakerData.speakerId) {
      EventBus.dispatch(CustomEvents.OpenContentSettings, { speakerId: speakerData.speakerId });
    }
  }, [speakerData.speakerId]);

  return (
    <Popover>
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="speaker-label-typography-button"
            className="group/button flex size-10 select-none items-center justify-center rounded-md hover:bg-slate-100"
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <IconTypography size={20} />
              <PlayerMenuTooltip content="Typography" />
            </div>
          </Popover.Button>
          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="flex select-none justify-start">
              <div className="absolute -right-24 mt-1.5 flex min-w-[200px] flex-col items-start gap-2 rounded-xl border border-gray-200 bg-white p-2.5">
                <Tab.Group>
                  <Tab.List className="flex h-8 w-full flex-row items-start rounded-lg bg-gray-100 p-0.5">
                    {[
                      { label: 'Name', value: 'name' },
                      { label: 'Title', value: 'title' }
                    ].map((tab, index) => (
                      <Tab
                        key={index}
                        className={({ selected }) =>
                          classnames(
                            'size-full rounded-md text-xs font-medium leading-5',
                            selected
                              ? 'bg-white text-black shadow'
                              : 'text-gray-500 hover:bg-white/[0.12] hover:text-gray-700'
                          )
                        }
                      >
                        {tab.label}
                      </Tab>
                    ))}
                  </Tab.List>
                  <Tab.Panels className="size-full flex-1">
                    <Tab.Panel>
                      <div className="flex w-full flex-col items-start gap-1.5">
                        {/* Warning for missing name */}
                        {!speakerData.hasName && (
                          <AlertBanner type="warning">
                            <button
                              onClick={handleEditSpeaker}
                              className="underline hover:text-amber-800 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:ring-offset-1"
                            >
                              Add a speaker name
                            </button>
                          </AlertBanner>
                        )}
                        {/* Font Selector for Name */}
                        <div className="flex h-8 w-full flex-row items-center gap-2">
                          <div className="max-w-[136px] flex-1 overflow-hidden">
                            <SpeakerLabelFontSelector
                              value={nameFontLocation}
                              onChange={handleNameFontChange}
                              clipId={clip.id}
                            />
                          </div>
                          <SpeakerLabelUppercaseToggle
                            value={nameStyle.textTransform === 'uppercase'}
                            onChange={value => updateNameStyle({ textTransform: value ? 'uppercase' : 'none' })}
                            tooltipId="name-uppercase-toggle-tooltip"
                          />
                        </div>

                        {/* Font Size */}
                        <div className="flex h-[43px] w-full flex-col items-start gap-1">
                          <label className="h-3 text-[10px] font-medium text-gray-400">Font size</label>
                          <SpeakerLabelFontSizeSelector
                            value={
                              nameStyle.fontSizePreset ||
                              cssToPreset(nameStyle.fontSize as string, DEFAULT_NAME_FONT_SIZE_PRESET)
                            }
                            onChange={(preset: FontSizePreset) =>
                              updateNameStyle({ fontSizePreset: preset, fontSize: undefined })
                            }
                          />
                        </div>

                        {/* Line Height and Letter Spacing */}
                        <div className="flex h-10 w-full flex-row items-start gap-2">
                          <div className="flex h-10 w-[86px] flex-col items-start gap-1">
                            <label className="h-3 text-[10px] font-medium text-gray-400">Line height</label>
                            <SpeakerLabelLineHeightInput
                              value={(nameStyle.lineHeight as string) || '1.3'}
                              onChange={value => updateNameStyle({ lineHeight: value })}
                            />
                          </div>
                          <div className="flex h-10 w-[86px] flex-col items-start gap-1">
                            <label className="h-3 text-[10px] font-medium text-gray-400">Letter spacing</label>
                            <SpeakerLabelLetterSpacingInput
                              value={(nameStyle.letterSpacing as string) || '0em'}
                              onChange={value => updateNameStyle({ letterSpacing: value })}
                            />
                          </div>
                        </div>
                      </div>
                    </Tab.Panel>
                    <Tab.Panel>
                      <div className="flex w-full flex-col items-start gap-1.5">
                        {/* Warning for missing title */}
                        {!speakerData.hasTitle && (
                          <AlertBanner type="warning">
                            <button
                              onClick={handleEditSpeaker}
                              className="underline hover:text-amber-800 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:ring-offset-1"
                            >
                              Add the speaker title
                            </button>
                          </AlertBanner>
                        )}
                        {/* Font Selector for Title */}
                        <div className="flex h-8 w-full flex-row items-center gap-2">
                          <div className="max-w-[136px] flex-1 overflow-hidden">
                            <SpeakerLabelFontSelector
                              value={titleFontLocation}
                              onChange={handleTitleFontChange}
                              clipId={clip.id}
                            />
                          </div>
                          <SpeakerLabelUppercaseToggle
                            value={titleStyle.textTransform === 'uppercase'}
                            onChange={value => updateTitleStyle({ textTransform: value ? 'uppercase' : 'none' })}
                            tooltipId="title-uppercase-toggle-tooltip"
                          />
                        </div>

                        {/* Font Size */}
                        <div className="flex h-[43px] w-full flex-col items-start gap-1">
                          <label className="h-3 text-[10px] font-medium text-gray-400">Font size</label>
                          <SpeakerLabelFontSizeSelector
                            value={
                              titleStyle.fontSizePreset ||
                              cssToPreset(titleStyle.fontSize as string, DEFAULT_TITLE_FONT_SIZE_PRESET)
                            }
                            onChange={(preset: FontSizePreset) =>
                              updateTitleStyle({ fontSizePreset: preset, fontSize: undefined })
                            }
                          />
                        </div>

                        {/* Line Height and Letter Spacing */}
                        <div className="flex h-10 w-full flex-row items-start gap-2">
                          <div className="flex h-10 w-[86px] flex-col items-start gap-1">
                            <label className="h-3 text-[10px] font-medium text-gray-400">Line height</label>
                            <SpeakerLabelLineHeightInput
                              value={(titleStyle.lineHeight as string) || '1.3'}
                              onChange={value => updateTitleStyle({ lineHeight: value })}
                            />
                          </div>
                          <div className="flex h-10 w-[86px] flex-col items-start gap-1">
                            <label className="h-3 text-[10px] font-medium text-gray-400">Letter spacing</label>
                            <SpeakerLabelLetterSpacingInput
                              value={(titleStyle.letterSpacing as string) || '0em'}
                              onChange={value => updateTitleStyle({ letterSpacing: value })}
                            />
                          </div>
                        </div>
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(SpeakerLabelTypographyMenu);
